// 头像占位符工具 - 生成静态SVG头像

/**
 * 生成用户头像占位符SVG
 * @param name 用户名称，用于生成颜色和首字母
 * @param size 头像尺寸，默认60px
 * @returns base64编码的SVG数据URI
 */
export const generateAvatar = (name: string, size: number = 60): string => {
  // 从名称生成颜色
  const colors = [
    '#F97316', // orange-500
    '#3B82F6', // blue-500
    '#10B981', // emerald-500
    '#8B5CF6', // violet-500
    '#F59E0B', // amber-500
    '#EF4444', // red-500
    '#06B6D4', // cyan-500
    '#84CC16', // lime-500
    '#EC4899', // pink-500
    '#6366F1', // indigo-500
  ];

  // 根据名称计算颜色索引
  const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  const backgroundColor = colors[colorIndex];
  const textColor = '#FFFFFF';

  // 获取首字母
  const initial = name.charAt(0).toUpperCase();

  // 生成SVG
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${backgroundColor}"/>
      <text
        x="${size/2}"
        y="${size/2 + size*0.1}"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="${textColor}"
        font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
        font-size="${size*0.4}"
        font-weight="500"
      >${initial}</text>
    </svg>
  `.trim().replace(/\s+/g, ' ');

  // 转换为base64
  const base64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64}`;
};

/**
 * 预定义的用户头像
 */
export const userAvatars = {
  张先生: generateAvatar('张先生'),
  李女士: generateAvatar('李女士'),
  王先生: generateAvatar('王先生'),
  陈女士: generateAvatar('陈女士'),
  刘先生: generateAvatar('刘先生'),
  赵女士: generateAvatar('赵女士'),
};

/**
 * 获取用户头像，如果没有预定义则动态生成
 * @param name 用户名称
 * @param size 头像尺寸
 * @returns 头像数据URI
 */
export const getUserAvatar = (name: string, size?: number): string => {
  if (name in userAvatars) {
    return userAvatars[name as keyof typeof userAvatars];
  }
  return generateAvatar(name, size);
};

/**
 * 运动主题图标SVG
 */
export const sportIcons = {
  // 跑步图标
  running: `data:image/svg+xml;base64,${btoa(`
    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="30" cy="30" r="30" fill="#F97316"/>
      <path d="M25 18C26.6569 18 28 16.6569 28 15C28 13.3431 26.6569 12 25 12C23.3431 12 22 13.3431 22 15C22 16.6569 23.3431 18 25 18Z" fill="white"/>
      <path d="M29 20L27 22V26L24 28L22 26V22L20 20L22 18L30 18L32 20L29 20Z" fill="white"/>
      <path d="M26 30L28 32L30 34L32 32L30 30L28 28L26 30Z" fill="white"/>
      <path d="M18 36L22 32L24 34L26 36L24 38L20 38L18 36Z" fill="white"/>
      <path d="M34 40L38 36L42 38L40 42L36 44L34 40Z" fill="white"/>
    </svg>
  `)}`,

  // 奖杯图标
  trophy: `data:image/svg+xml;base64,${btoa(`
    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="30" cy="30" r="30" fill="#EAB308"/>
      <path d="M30 15L32 25H38L33 29L35 39L30 35L25 39L27 29L22 25H28L30 15Z" fill="white"/>
      <rect x="26" y="40" width="8" height="4" fill="white"/>
      <rect x="22" y="44" width="16" height="2" fill="white"/>
    </svg>
  `)}`,

  // 心脏图标
  heart: `data:image/svg+xml;base64,${btoa(`
    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="30" cy="30" r="30" fill="#EF4444"/>
      <path d="M30 45L26.5 41.8C19 35 15 31.2 15 26.5C15 22.5 18 19.5 22 19.5C24.2 19.5 26.3 20.6 27.5 22.3C28.7 20.6 30.8 19.5 33 19.5C37 19.5 40 22.5 40 26.5C40 31.2 36 35 28.5 41.8L30 45Z" fill="white"/>
    </svg>
  `)}`,
};

/**
 * 生成小程序二维码占位符
 */
export const generateQRCode = (): string => {
  const svg = `
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="200" height="200" rx="12" fill="white"/>
      <rect x="20" y="20" width="160" height="160" fill="#000000" opacity="0.05"/>

      <!-- 定位角 -->
      <rect x="30" y="30" width="40" height="40" fill="black"/>
      <rect x="36" y="36" width="28" height="28" fill="white"/>
      <rect x="42" y="42" width="16" height="16" fill="black"/>

      <rect x="130" y="30" width="40" height="40" fill="black"/>
      <rect x="136" y="36" width="28" height="28" fill="white"/>
      <rect x="142" y="42" width="16" height="16" fill="black"/>

      <rect x="30" y="130" width="40" height="40" fill="black"/>
      <rect x="36" y="136" width="28" height="28" fill="white"/>
      <rect x="42" y="142" width="16" height="16" fill="black"/>

      <!-- 模拟数据区域 -->
      <rect x="80" y="40" width="6" height="6" fill="black"/>
      <rect x="90" y="40" width="6" height="6" fill="black"/>
      <rect x="100" y="40" width="6" height="6" fill="black"/>
      <rect x="110" y="40" width="6" height="6" fill="black"/>

      <rect x="40" y="80" width="6" height="6" fill="black"/>
      <rect x="50" y="80" width="6" height="6" fill="black"/>
      <rect x="60" y="90" width="6" height="6" fill="black"/>
      <rect x="70" y="90" width="6" height="6" fill="black"/>

      <rect x="80" y="80" width="6" height="6" fill="black"/>
      <rect x="90" y="90" width="6" height="6" fill="black"/>
      <rect x="100" y="80" width="6" height="6" fill="black"/>
      <rect x="110" y="90" width="6" height="6" fill="black"/>
      <rect x="120" y="80" width="6" height="6" fill="black"/>

      <rect x="80" y="110" width="6" height="6" fill="black"/>
      <rect x="90" y="110" width="6" height="6" fill="black"/>
      <rect x="100" y="120" width="6" height="6" fill="black"/>
      <rect x="110" y="110" width="6" height="6" fill="black"/>
      <rect x="120" y="120" width="6" height="6" fill="black"/>

      <rect x="130" y="80" width="6" height="6" fill="black"/>
      <rect x="140" y="90" width="6" height="6" fill="black"/>
      <rect x="150" y="80" width="6" height="6" fill="black"/>
      <rect x="160" y="90" width="6" height="6" fill="black"/>

      <!-- 中央图标 -->
      <circle cx="100" cy="100" r="15" fill="#F97316"/>
      <path d="M95 95L100 90L105 95L100 105L95 95Z" fill="white"/>

      <!-- 底部文字 -->
      <text x="100" y="185" text-anchor="middle" fill="#666666" font-family="system-ui" font-size="12">微信小程序</text>
    </svg>
  `.trim().replace(/\s+/g, ' ');

  const base64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64}`;
};

/**
 * 导出常用的占位符
 */
export const placeholders = {
  userAvatar: (name: string) => getUserAvatar(name),
  qrCode: generateQRCode(),
  runningIcon: sportIcons.running,
  trophyIcon: sportIcons.trophy,
  heartIcon: sportIcons.heart,
};
